import type { ComicProjectCreative } from '../comicDomainTypes'
import {
  COMIC_PANEL_REF_STORAGE_MAX,
  normalizeCharacterReferenceUrls,
  normalizePanelReferenceUrls,
} from '../comicReferenceLimits'
import type { ComicPanel } from '../comicTypes'
import { allCharacterCards, collectPageCast, shortCharacterAppearance } from './pageCast'
import type { ImagePromptRefBinding } from './types'

export type PageSceneRef = {
  url: string
  /** 本页画格序号（1-based，与【本页分镜】格号一致） */
  slotNo: number
}

export type PageReferencePlan = {
  sceneRefs: PageSceneRef[]
  sceneUrls: string[]
  characterBindings: {
    characterId: string
    name: string
    url: string | null
    shortAppearance: string
  }[]
}

/** 按本页分镜顺序收集场景参考（全页合计上限 COMIC_PANEL_REF_STORAGE_MAX） */
export function collectPageSceneRefsWithSlots(panels: ComicPanel[]): PageSceneRef[] {
  const seen = new Set<string>()
  const out: PageSceneRef[] = []
  for (let i = 0; i < panels.length; i += 1) {
    const panel = panels[i]
    if (!panel) continue
    const slotNo = i + 1
    for (const u of normalizePanelReferenceUrls(panel.referenceImageUrls)) {
      if (seen.has(u)) continue
      seen.add(u)
      out.push({ url: u, slotNo })
      if (out.length >= COMIC_PANEL_REF_STORAGE_MAX) return out
    }
  }
  return out
}

export function collectPageSceneReferenceUrls(panels: ComicPanel[]): string[] {
  return collectPageSceneRefsWithSlots(panels).map((r) => r.url)
}

export function buildPageReferencePlan(
  creative: ComicProjectCreative,
  panels: ComicPanel[],
): PageReferencePlan {
  const sceneRefs = collectPageSceneRefsWithSlots(panels)
  const cast = collectPageCast(creative, panels)
  const characterBindings = cast.map((card) => {
    const url = card.referenceImageUrl?.trim() || null
    return {
      characterId: card.id,
      name: card.name.trim() || '角色',
      url,
      shortAppearance: shortCharacterAppearance(card, Boolean(url)),
    }
  })
  return {
    sceneRefs,
    sceneUrls: sceneRefs.map((r) => r.url),
    characterBindings,
  }
}

/**
 * 合并本页场景参考 + 出场角色造型参考（先场景后角色），受模型 maxReferenceImages 限制。
 * 与分镜 UI「场景与造型叠加、互不替代」一致。
 */
export function resolvePageReferenceUrls(
  creative: ComicProjectCreative,
  panels: ComicPanel[],
  maxCount: number,
): { urls: string[]; bindings: ImagePromptRefBinding[] } {
  const plan = buildPageReferencePlan(creative, panels)
  const cap = Math.max(0, maxCount)
  if (cap === 0) {
    return { urls: [], bindings: [] }
  }

  const urls: string[] = []
  const bindings: ImagePromptRefBinding[] = []
  const used = new Set<string>()

  const pushBinding = (entry: ImagePromptRefBinding) => {
    if (urls.length >= cap) return false
    if (used.has(entry.url)) return false
    used.add(entry.url)
    const index = urls.length + 1
    urls.push(entry.url)
    bindings.push({ ...entry, index })
    return true
  }

  for (const scene of plan.sceneRefs) {
    if (urls.length >= cap) break
    pushBinding({
      index: 0,
      kind: 'scene',
      name: `第${scene.slotNo}格场景`,
      url: scene.url,
      panelSlot: scene.slotNo,
    })
  }

  for (const b of plan.characterBindings) {
    if (urls.length >= cap) break
    if (!b.url) continue
    pushBinding({
      index: 0,
      kind: 'character',
      characterId: b.characterId,
      name: b.name,
      url: b.url,
    })
  }

  if (bindings.length > 0) {
    return { urls, bindings }
  }

  if (plan.characterBindings.length === 0) {
    const fallback = resolveProjectCharacterReferenceUrls(creative).slice(0, cap)
    const fallbackBindings: ImagePromptRefBinding[] = fallback.map((url, i) => ({
      index: i + 1,
      kind: 'character',
      name: `角色参考${i + 1}`,
      url,
    }))
    return { urls: fallback, bindings: fallbackBindings }
  }

  return { urls, bindings }
}

/** 无本页 cast 时回落全书有参考图的角色 */
export function resolveProjectCharacterReferenceUrls(creative: ComicProjectCreative): string[] {
  return normalizeCharacterReferenceUrls(
    allCharacterCards(creative)
      .map((c) => c.referenceImageUrl)
      .filter((u): u is string => Boolean(u?.trim())),
  )
}
